import type {
  FighterState,
  FacingDirection,
  AttackType,
  FighterStats,
  InputState,
  HitBox,
} from '@/shared/types';
import { GROUND_Y, GRAVITY, MAX_HEALTH, CANVAS_WIDTH } from '@/shared/constants';

export abstract class Fighter {
  x: number;
  y: number;
  velocityX = 0;
  velocityY = 0;
  health: number;
  state: FighterState = 'idle';
  facing: FacingDirection;
  attackTimer = 0;
  hitStunTimer = 0;
  currentAttack: AttackType | null = null;

  abstract readonly stats: FighterStats;
  abstract readonly color: string;
  abstract readonly name: string;

  constructor(x: number, facing: FacingDirection) {
    this.x = x;
    this.y = GROUND_Y;
    this.facing = facing;
    this.health = MAX_HEALTH;
  }

  get width(): number {
    return this.stats.width;
  }

  get height(): number {
    return this.stats.height;
  }

  get isGrounded(): boolean {
    return this.y >= GROUND_Y;
  }

  get isAttacking(): boolean {
    return this.state === 'attack' && this.attackTimer > 0;
  }

  get isBlocking(): boolean {
    return this.state === 'block';
  }

  getHitBox(): HitBox {
    return {
      x: this.x - this.width / 2,
      y: this.y - this.height,
      width: this.width,
      height: this.height,
    };
  }

  getAttackHitBox(): HitBox | null {
    if (!this.isAttacking || !this.currentAttack) return null;

    const attack = this.stats.attacks[this.currentAttack];
    const attackFrames = attack.startup + attack.active + attack.recovery;
    const elapsed = attackFrames - this.attackTimer;

    if (elapsed < attack.startup || elapsed >= attack.startup + attack.active) {
      return null;
    }

    const offsetX = this.facing === 'right' ? this.width / 2 : -this.width / 2 - attack.range;
    return {
      x: this.x + offsetX,
      y: this.y - this.height + attack.hitBox.y,
      width: attack.range,
      height: attack.hitBox.height,
    };
  }

  applyInput(input: InputState): void {
    if (this.state === 'hit' || this.state === 'ko') return;

    if (this.isAttacking) return;

    if (input.block && this.isGrounded) {
      this.state = 'block';
      this.velocityX = 0;
      return;
    }

    if (input.attack1) {
      this.startAttack('bite');
      return;
    }
    if (input.attack2) {
      this.startAttack('tail-swipe');
      return;
    }
    if (input.special) {
      this.startAttack('special');
      return;
    }

    if (input.up && this.isGrounded) {
      this.velocityY = this.stats.jumpForce;
      this.state = 'jump';
    }

    if (input.left) {
      this.velocityX = -this.stats.speed;
      if (this.isGrounded) this.state = 'walk';
    } else if (input.right) {
      this.velocityX = this.stats.speed;
      if (this.isGrounded) this.state = 'walk';
    } else {
      this.velocityX = 0;
      if (this.isGrounded && this.state !== 'jump') {
        this.state = 'idle';
      }
    }
  }

  private startAttack(type: AttackType): void {
    const attack = this.stats.attacks[type];
    this.currentAttack = type;
    this.attackTimer = attack.startup + attack.active + attack.recovery;
    this.state = 'attack';
    this.velocityX = 0;
  }

  takeDamage(damage: number): void {
    if (this.isBlocking) {
      this.health -= Math.floor(damage * 0.1);
      return;
    }

    this.health -= damage;
    this.hitStunTimer = 15;
    this.state = 'hit';
    this.velocityX = this.facing === 'right' ? -3 : 3;

    if (this.health <= 0) {
      this.health = 0;
      this.state = 'ko';
    }
  }

  update(): void {
    if (this.state === 'ko') return;

    if (this.hitStunTimer > 0) {
      this.hitStunTimer--;
      if (this.hitStunTimer === 0 && this.health > 0) {
        this.state = 'idle';
      }
    }

    if (this.attackTimer > 0) {
      this.attackTimer--;
      if (this.attackTimer === 0) {
        this.state = 'idle';
        this.currentAttack = null;
      }
    }

    this.velocityY += GRAVITY;
    this.x += this.velocityX;
    this.y += this.velocityY;

    if (this.y >= GROUND_Y) {
      this.y = GROUND_Y;
      this.velocityY = 0;
      if (this.state === 'jump') {
        this.state = 'idle';
      }
    }

    this.x = Math.max(this.width / 2, Math.min(CANVAS_WIDTH - this.width / 2, this.x));
  }

  reset(x: number, facing: FacingDirection): void {
    this.x = x;
    this.y = GROUND_Y;
    this.velocityX = 0;
    this.velocityY = 0;
    this.health = MAX_HEALTH;
    this.state = 'idle';
    this.facing = facing;
    this.attackTimer = 0;
    this.hitStunTimer = 0;
    this.currentAttack = null;
  }
}
